import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Container,
  MenuItem,
  InputAdornment,
  FormHelperText,
} from "@material-ui/core";
import { FormikHelpers, useFormik } from "formik";
import * as Yup from "yup";
import { FormattedMessage, useIntl } from "react-intl";
import { IoIosCalendar, IoIosPin } from "react-icons/io";
import moment from "moment";
import { PhoneNumber } from "libphonenumber-js/types";

import { PageTitle } from "../../components/page-title/PageTitle";
import { SignupLayout } from "../../common/SignupLayout";
import { useStyles } from "./CreateBusinessScreen.style";
import { SignupContextProvider } from "../../context/SignupContext";
import { FormHeader } from "../signup/components/FormHeader";
import Currency from "../../models/Currency";
import BusinessCategory from "../../models/BusinessCategory";
import BusinessCategoryService from "../../services/BusinessCategoryService";
import CurrencyService from "../../services/CurrencyService";
import {
  BusinessAttributes,
  BusinessTypeEnum,
} from "../../attributes/business-attributes";
import BusinessService from "../../services/BusinessService";
import {
  base64,
  formatPhoneNumber,
  phoneNumberToCountry,
  useQuery,
  validateFile,
} from "../../common/Hooks";
import { InputField } from "../../components/input-field/InputField";
import { InputSelect } from "../../components/input-select/InputSelect";
import { PhoneNumberInput } from "../../components/phone-number/PhoneNumberInput";
import Country from "../../models/Country";
import { ImageChooser } from "../../components/image-chooser/ImageChooser";
import { InputButton } from "../../components/input-button/InputButton";
import { Overlay } from "../../components/overlay/Overlay";
import { HomeScreen } from "../home/HomeScreen";
import {
  useAuthenticationContext,
  UserActionTypeEnum,
} from "../../context/UserContext";
import { SlideWrapper } from "../../components/slide-wrapper/SlideWrapper";
import { InputDate } from "../../components/input-date/InputDate";
import {
  FILE_MAX_SIZE,
  FILE_MAX_SIZE_STR,
  YYYY_MM_DD_FORMAT,
} from "../../settings/constants";

export const CreateBusinessScreen = (props: any) => {
  const classes = useStyles(props);
  const intl = useIntl();
  const history = useHistory();
  const queryParams = useQuery();
  // State
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [currencies, setCurrency] = useState((): Array<Currency> => null);
  const [businessCategories, setBusinessCategories] = useState(
    (): Array<BusinessCategory> => null
  );
  // Context
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [user, token, authDispatch] = useAuthenticationContext();
  // Others
  const userPhoneDetails = phoneNumberToCountry(user.user_detail.telephone);

  const initPrimaryCountry = () => {
    return userPhoneDetails !== null
      ? userPhoneDetails.country
      : formik.values.primary_phone_number
      ? new Country(
          "",
          formik.values.primary_phone_number_dial_code,
          formik.values.primary_phone_number_iso_code.toLowerCase()
        )
      : Country.defaultCountry("");
  };

  const initPrimaryPhoneNumber = () => {
    return userPhoneDetails !== null ? userPhoneDetails.number : "";
  };

  const loadBusinessCategories = () => {
    const response = BusinessCategoryService.supportedBusinessCategories();

    response.then((response) => {
      let results: Array<BusinessCategory> = [];

      if (response.isSuccess && response.data.code === 100) {
        JSON.parse(JSON.stringify(response.data.data)).forEach(
          (item, index) => {
            results.push(BusinessCategory.fromJson(item));
          }
        );
        setBusinessCategories(results);
      } else {
        setBusinessCategories([]);
      }
    });
  };

  const loadCurrencies = () => {
    const response = CurrencyService.supportedCurrencies();

    response.then((response) => {
      let results: Array<Currency> = [];

      if (response.isSuccess && response.data.code === 100) {
        JSON.parse(JSON.stringify(response.data.data)).forEach(
          (item, index) => {
            results.push(Currency.fromJson(item));
          }
        );
        setCurrency(results);
      } else {
        setCurrency([]);
      }
    });
  };

  const handleAttachmentSelection = (name: string, file: File) => {
    if (file !== null) {
      base64(file, (result) => {
        formik.values[name] = result;
      });
    } else {
      formik.values[name] = null;
    }
    formik.setErrors({ ...formik.errors, [name]: undefined });
  };

  const handleBusinessType = (event: any, child: any) => {
    formik.setValues({ ...formik.values, business_type: event.target.value });
  };

  const handlePhoneChange = (phoneNumber: PhoneNumber | null) => {
    if (phoneNumber !== null) {
      formik.values.primary_phone_number = phoneNumber.number.toString();
      formik.values.primary_phone_number_dial_code = `+${phoneNumber.countryCallingCode}`;
      formik.values.primary_phone_number_iso_code = phoneNumber.country
        .toString()
        .toUpperCase();

      if (!phoneNumber.isValid()) {
        formik.setErrors({
          ...formik.errors,
          primary_phone_number: intl.formatMessage({
            id: "error.invalidPhoneNumber",
          }),
        });
      } else {
        formik.setErrors({ ...formik.errors, primary_phone_number: undefined });
      }
    } else {
      formik.values.primary_phone_number = "";
      formik.setErrors({ ...formik.errors, primary_phone_number: undefined });
    }
  };

  const handleDateChange = (name: string, dateString: string) => {
    formik.setValues({ ...formik.values, [name]: dateString });
  };

  const formValidation = () => {
    return {
      business_category_id: Yup.number().required(
        intl.formatMessage({ id: "error.categoryCompany" })
      ),
      business_type: Yup.number().required(
        intl.formatMessage({ id: "error.businessType" })
      ),
      company_name: Yup.string()
        .max(100, intl.formatMessage({ id: "error.tooLong" }))
        .required(intl.formatMessage({ id: "error.companyName" })),
      company_email: Yup.string()
        .required(intl.formatMessage({ id: "error.email" }))
        .email(intl.formatMessage({ id: "error.email" })),
      primary_phone_number: Yup.string().required(
        intl.formatMessage({ id: "error.invalidPhoneNumber" })
      ),
      start_date: Yup.string().required(
        intl.formatMessage({ id: "error.startDate" })
      ),
      company_address: Yup.string().required(
        intl.formatMessage({ id: "error.companyAddress" })
      ),
      registration_number: Yup.string()
        .max(100, intl.formatMessage({ id: "error.tooLong" }))
        .required(intl.formatMessage({ id: "error.enterValidRegNum" })),
      tax_payer_number: Yup.string()
        .max(100, intl.formatMessage({ id: "error.tooLong" }))
        .required(intl.formatMessage({ id: "error.enterValidTaxNum" })),
      bic_number: Yup.string()
        .max(100, intl.formatMessage({ id: "error.tooLong" }))
        .required(intl.formatMessage({ id: "error.enterValidBicNum" })),
      rib_number: Yup.string()
        .max(100, intl.formatMessage({ id: "error.tooLong" }))
        .required(intl.formatMessage({ id: "error.enterValidRibNum" })),

      company_logo: Yup.mixed()
        .required(intl.formatMessage({ id: "error.selectValidPhoto" }))
        .test(
          "fileSize",
          intl.formatMessage(
            { id: "error.tooLargePicture" },
            { max: FILE_MAX_SIZE_STR }
          ),
          (value) => {
            return value !== null && validateFile(value, FILE_MAX_SIZE);
          }
        ),
      id_bank_photo: Yup.mixed()
        .required(intl.formatMessage({ id: "error.selectValidPhoto" }))
        .test(
          "fileSize",
          intl.formatMessage(
            { id: "error.tooLargePicture" },
            { max: FILE_MAX_SIZE_STR }
          ),
          (value) => {
            return value !== null && validateFile(value, FILE_MAX_SIZE);
          }
        ),
      registration_photo: Yup.mixed()
        .required(intl.formatMessage({ id: "error.selectValidPhoto" }))
        .test(
          "fileSize",
          intl.formatMessage(
            { id: "error.tooLargePicture" },
            { max: FILE_MAX_SIZE_STR }
          ),
          (value) => {
            return value !== null && validateFile(value, FILE_MAX_SIZE);
          }
        ),
      tax_payer_photo: Yup.mixed()
        .required(intl.formatMessage({ id: "error.selectValidPhoto" }))
        .test(
          "fileSize",
          intl.formatMessage(
            { id: "error.tooLargePicture" },
            { max: FILE_MAX_SIZE_STR }
          ),
          (value) => {
            return value !== null && validateFile(value, FILE_MAX_SIZE);
          }
        ),
    };
  };

  const initFormValues = () => {
    return {
      primary_phone_number_dial_code: userPhoneDetails.country.dial_code,
      primary_phone_number_iso_code: userPhoneDetails.country.iso_code,
      primary_phone_number: initPrimaryPhoneNumber(),
      business_type: BusinessTypeEnum.personal,
      business_category_id: "",
      company_name: queryParams.get("name") ?? "",
      company_email: "",
      start_date: "",
      company_address: "",
      registration_number: "",
      tax_payer_number: "",
      rib_number: "",
      bic_number: "",
      registration_photo: null,
      tax_payer_photo: null,
      id_bank_photo: null,
      company_logo: null,
    };
  };

  const formik = useFormik({
    initialValues: initFormValues(),
    validationSchema: Yup.object().shape({
      ...formValidation(),
    }),
    onSubmit: async (
      values: BusinessAttributes,
      { setErrors, setSubmitting }: FormikHelpers<any>
    ) => {
      try {
        const registration = await BusinessService.registerBusiness(values);
        setSubmitting(false);

        if (registration.isSuccess) {
          switch (registration.data.code) {
            case 100: // Ok
              authDispatch({
                type: UserActionTypeEnum.addBusiness,
                data: JSON.parse(JSON.stringify(registration.data.data)),
              });
              history.replace(HomeScreen.routeName);
              break;
            case 103: // Unknown error
              setErrors({ unknown: registration.data.message });
              break;
            default:
              // validation error
              setErrors(JSON.parse(JSON.stringify(registration.data.data)));
              break;
          }
        } else {
          setErrors({
            unknown: intl.formatMessage({ id: "sharedScreen.unknownError" }),
          });
        }
      } catch (error) {
        setSubmitting(false);
        setErrors({
          unknown: intl.formatMessage({ id: "sharedScreen.unknownError" }),
        });
      }
    },
  });

  useEffect(() => {
    loadCurrencies();
    loadBusinessCategories();
  }, []);

  return (
    <>
      <PageTitle
        title={intl.formatMessage({ id: "sharedScreen.createBusiness" })}
      />
      <SignupContextProvider>
        <SignupLayout mode="dark" withMenu>
          <Container maxWidth="sm">
            <Box className={classes.root}>
              <Container maxWidth="sm" className={classes.contentWrapper}>
                <SlideWrapper>
                  <FormHeader>
                    <div>
                      <Box component="span" className="title">
                        <FormattedMessage id="sharedScreen.createBusiness" />
                      </Box>
                      <Box component="span" className="subTitle">
                        <FormattedMessage id="signupBusinessOwner.title" />
                      </Box>
                    </div>
                  </FormHeader>

                  <form
                    noValidate
                    autoComplete="off"
                    onSubmit={formik.handleSubmit}
                  >
                    <Box mt={6}>
                      <Box mb={2}>
                        <InputField
                          fullWidth
                          id="company_name"
                          name="company_name"
                          label={intl.formatMessage({
                            id: "sharedScreen.companyName",
                          })}
                          placeholder={intl.formatMessage({
                            id: "sharedScreen.enterCompanyName",
                          })}
                          disabled
                          type="text"
                          onChange={formik.handleChange}
                          value={formik.values.company_name}
                          error={
                            Boolean(formik.touched.company_name) &&
                            Boolean(formik.errors.company_name)
                          }
                          helperText={
                            formik.touched.company_name &&
                            formik.errors.company_name
                          }
                        />
                      </Box>
                      <Box mb={2}>
                        <InputSelect
                          fullWidth
                          id="business_type"
                          name="business_type"
                          value={formik.values.business_type}
                          label={intl.formatMessage({
                            id: "sharedScreen.businessType",
                          })}
                          onChange={handleBusinessType}
                          error={
                            Boolean(formik.touched.business_type) &&
                            Boolean(formik.errors.business_type)
                          }
                          helperText={
                            formik.touched.business_type &&
                            formik.errors.business_type
                          }
                        >
                          <MenuItem value="" disabled>
                            <FormattedMessage id="sharedScreen.selectBusinessType" />
                          </MenuItem>
                          <MenuItem value={BusinessTypeEnum.personal}>
                            <FormattedMessage id="sharedScreen.personalBusinessType" />
                          </MenuItem>
                          <MenuItem value={BusinessTypeEnum.enterprise}>
                            <FormattedMessage id="sharedScreen.enterpriseBusinessType" />
                          </MenuItem>
                        </InputSelect>
                      </Box>
                      <Box mb={2}>
                        <InputField
                          fullWidth
                          id="company_email"
                          name="company_email"
                          label={intl.formatMessage({
                            id: "sharedScreen.email",
                          })}
                          placeholder={intl.formatMessage({
                            id: "sharedScreen.enterCompanyEmail",
                          })}
                          type="text"
                          onChange={formik.handleChange}
                          value={formik.values.company_email}
                          error={
                            Boolean(formik.touched.company_email) &&
                            Boolean(formik.errors.company_email)
                          }
                          helperText={
                            formik.touched.company_email &&
                            formik.errors.company_email
                          }
                        />
                      </Box>
                      <Box mb={2}>
                        <InputField
                          fullWidth
                          id="company_address"
                          name="company_address"
                          label={intl.formatMessage({
                            id: "sharedScreen.address",
                          })}
                          placeholder={intl.formatMessage({
                            id: "sharedScreen.companyAddress",
                          })}
                          type="text"
                          onChange={formik.handleChange}
                          value={formik.values.company_address}
                          error={
                            Boolean(formik.touched.company_address) &&
                            Boolean(formik.errors.company_address)
                          }
                          helperText={
                            formik.touched.company_address &&
                            formik.errors.company_address
                          }
                          endAdornment={
                            <InputAdornment position="end">
                              <IoIosPin />
                            </InputAdornment>
                          }
                        />
                      </Box>
                      <Box mb={2}>
                        <PhoneNumberInput
                          id="primary_phone_number"
                          name="primary_phone_number"
                          label={intl.formatMessage({
                            id: "sharedScreen.phoneNumber",
                          })}
                          defaultCountry={initPrimaryCountry()}
                          defaultPhone={
                            userPhoneDetails !== null
                              ? userPhoneDetails.nationalValue
                              : formatPhoneNumber(
                                  formik.values.primary_phone_number,
                                  false
                                )
                          }
                          error={
                            formik.touched.primary_phone_number &&
                            formik.errors.primary_phone_number
                          }
                          onChange={handlePhoneChange}
                        />
                      </Box>
                      <Box mb={4}>
                        <InputDate
                          maxDate={moment().format(YYYY_MM_DD_FORMAT)}
                          onDateChange={(name, date) =>
                            handleDateChange(name, date)
                          }
                          inputProps={{
                            fullWidth: true,
                            id: "start_date",
                            name: "start_date",
                            label: intl.formatMessage({
                              id: "sharedScreen.startDate",
                            }),
                            placeholder: intl.formatMessage({
                              id: "sharedScreen.enterCompanyStartDate",
                            }),
                            endAdornment: (
                              <InputAdornment position="end">
                                <IoIosCalendar />
                              </InputAdornment>
                            ),

                            value: formik.values.start_date,
                            error:
                              Boolean(formik.touched.start_date) &&
                              Boolean(formik.errors.start_date),
                            helperText:
                              formik.touched.start_date &&
                              formik.errors.start_date,
                          }}
                          pickerProps={{
                            format: YYYY_MM_DD_FORMAT,
                            disabled: false,
                            showToday: false,
                          }}
                        />
                      </Box>
                      <Box mb={2}>
                        <Box className="inlineFormElement">
                          <InputSelect
                            fullWidth
                            id="business_category_id"
                            name="business_category_id"
                            label={intl.formatMessage({
                              id: "sharedScreen.businessCategory",
                            })}
                            value={formik.values.business_category_id}
                            onChange={formik.handleChange}
                            error={
                              Boolean(formik.touched.business_category_id) &&
                              Boolean(formik.errors.business_category_id)
                            }
                            helperText={
                              formik.touched.business_category_id &&
                              formik.errors.business_category_id
                            }
                          >
                            <MenuItem value="" disabled>
                              {businessCategories === null ? (
                                <FormattedMessage id="sharedScreen.loading" />
                              ) : (
                                <FormattedMessage id="sharedScreen.selectCategory" />
                              )}
                            </MenuItem>
                            {(businessCategories === null
                              ? []
                              : businessCategories
                            ).map((item, i) => (
                              <MenuItem key={i} value={item.id}>
                                {item.designation}
                              </MenuItem>
                            ))}
                          </InputSelect>
                        </Box>
                      </Box>
                      <Box mb={2}>
                        <InputField
                          fullWidth
                          id="registration_number"
                          name="registration_number"
                          label={intl.formatMessage({
                            id: "sharedScreen.regNum",
                          })}
                          placeholder={intl.formatMessage({
                            id: "sharedScreen.enterRegNum",
                          })}
                          type="text"
                          onChange={formik.handleChange}
                          value={formik.values.registration_number}
                          error={
                            Boolean(formik.touched.registration_number) &&
                            Boolean(formik.errors.registration_number)
                          }
                          helperText={
                            formik.touched.registration_number &&
                            formik.errors.registration_number
                          }
                        />
                      </Box>
                      <Box mb={2}>
                        <InputField
                          fullWidth
                          id="tax_payer_number"
                          name="tax_payer_number"
                          label={intl.formatMessage({
                            id: "sharedScreen.taxNum",
                          })}
                          placeholder={intl.formatMessage({
                            id: "sharedScreen.enterTaxNum",
                          })}
                          type="text"
                          onChange={formik.handleChange}
                          value={formik.values.tax_payer_number}
                          error={
                            Boolean(formik.touched.tax_payer_number) &&
                            Boolean(formik.errors.tax_payer_number)
                          }
                          helperText={
                            formik.touched.tax_payer_number &&
                            formik.errors.tax_payer_number
                          }
                        />
                      </Box>
                      <Box
                        className="inlineForm"
                        mb={2}
                        display="flex"
                        flexWrap="nowrap"
                        alignItems="start"
                      >
                        <Box className="inlineFormElement">
                          <InputField
                            fullWidth
                            id="rib_number"
                            name="rib_number"
                            label={intl.formatMessage({
                              id: "sharedScreen.ribNum",
                            })}
                            placeholder={intl.formatMessage({
                              id: "sharedScreen.enterRibNum",
                            })}
                            type="text"
                            onChange={formik.handleChange}
                            value={formik.values.rib_number}
                            error={
                              Boolean(formik.touched.rib_number) &&
                              Boolean(formik.errors.rib_number)
                            }
                            helperText={
                              formik.touched.rib_number &&
                              formik.errors.rib_number
                            }
                          />
                        </Box>
                        <Box width={24} />
                        <Box className="inlineFormElement">
                          <InputField
                            fullWidth
                            id="bic_number"
                            name="bic_number"
                            label={intl.formatMessage({
                              id: "sharedScreen.bicNum",
                            })}
                            placeholder={intl.formatMessage({
                              id: "sharedScreen.enterBicNum",
                            })}
                            type="text"
                            onChange={formik.handleChange}
                            value={formik.values.bic_number}
                            error={
                              Boolean(formik.touched.bic_number) &&
                              Boolean(formik.errors.bic_number)
                            }
                            helperText={
                              formik.touched.bic_number &&
                              formik.errors.bic_number
                            }
                          />
                        </Box>
                      </Box>
                      <Box component="div" mt={6} className={classes.subHeader}>
                        <span>
                          <FormattedMessage id="sharedScreen.attachment" />
                        </span>
                        <p>
                          <FormattedMessage id="sharedScreen.tapToUpload" />
                        </p>
                      </Box>
                      <Box mb={2}>
                        <ImageChooser
                          variant="square"
                          onImageSelected={(file) =>
                            handleAttachmentSelection("company_logo", file)
                          }
                          label={intl.formatMessage({
                            id: "sharedScreen.addCompanyLogo",
                          })}
                        />
                        {formik.touched.company_logo &&
                          formik.errors.company_logo && (
                            <FormHelperText className="errorWrapper">
                              {formik.errors.company_logo}
                            </FormHelperText>
                          )}
                      </Box>
                      <Box mb={2}>
                        <ImageChooser
                          variant="square"
                          onImageSelected={(file) =>
                            handleAttachmentSelection(
                              "registration_photo",
                              file
                            )
                          }
                          label={intl.formatMessage({
                            id: "sharedScreen.addRegistrationPhoto",
                          })}
                        />
                        {formik.touched.registration_photo &&
                          formik.errors.registration_photo && (
                            <FormHelperText className="errorWrapper">
                              {formik.errors.registration_photo}
                            </FormHelperText>
                          )}
                      </Box>
                      <Box mb={2}>
                        <ImageChooser
                          variant="square"
                          onImageSelected={(file) =>
                            handleAttachmentSelection("tax_payer_photo", file)
                          }
                          label={intl.formatMessage({
                            id: "sharedScreen.addTaxPayerPhoto",
                          })}
                        />
                        {formik.touched.tax_payer_photo &&
                          formik.errors.tax_payer_photo && (
                            <FormHelperText className="errorWrapper">
                              {formik.errors.tax_payer_photo}
                            </FormHelperText>
                          )}
                      </Box>
                      <Box mb={2}>
                        <ImageChooser
                          variant="square"
                          onImageSelected={(file) =>
                            handleAttachmentSelection("id_bank_photo", file)
                          }
                          label={intl.formatMessage({
                            id: "sharedScreen.addBankAccountPhoto",
                          })}
                        />
                        {formik.touched.id_bank_photo &&
                          formik.errors.id_bank_photo && (
                            <FormHelperText className="errorWrapper">
                              {formik.errors.id_bank_photo}
                            </FormHelperText>
                          )}
                      </Box>

                      <Box
                        mt={10}
                        display="flex"
                        alignItems="center"
                        justifyContent="flex-end"
                      >
                        <Box width={100}>
                          <InputButton
                            color="secondary"
                            fullWidth
                            size="medium"
                            variant="contained"
                            type="submit"
                          >
                            <FormattedMessage id="sharedScreen.next" />
                          </InputButton>
                        </Box>
                      </Box>
                    </Box>
                  </form>
                </SlideWrapper>
                <Overlay loading={formik.isSubmitting} />
              </Container>
            </Box>
          </Container>
        </SignupLayout>
      </SignupContextProvider>
    </>
  );
};

CreateBusinessScreen.routeName = "/create-business";
